<template>
  <div class="vz-direction-view">
    <div class="text-h1 vz-direction-view--title">
      {{ titleCard }}
    </div>

    <div class="vz-gateway-view">
      <template v-if="isNetwork">
        <div>
          <vz-icon name="domain" variant="primary" size="16" />
          <span>Торговая сеть: {{ tradingNetworkName }}</span>
        </div>
        <div>
          <vz-icon name="warehouse" variant="primary" size="16" />
          <span>Филиал: {{ gatewayAddress }}</span>
        </div>
      </template>
      <template v-else>
        <div>
          <vz-icon v-bind="gatewayAddressOptions" size="16" />
          <span>{{ gatewayAddressLabel }}: {{ gatewayAddress }} <a
            v-if="terminalGuid"
            target="_blank"
            :href="`/mapScheme?g=${terminalGuid}`"
          >подробнее</a></span>
        </div>
      </template>
      <div>
        <vz-icon name="calendar-range" variant="primary" size="16" />
        <span>{{ gatewayDate }}</span>
      </div>
      <div>
        <vz-icon name="clock-outline" variant="primary" size="16" />
        <span>{{ gatewayTime }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {OrderForm, OrderView} from '~/types/types';
import VzIcon from '~/components/ui-kit/vz-icon.vue';
import {PropType} from 'vue';
import {useTerminalStore} from '~/stores/terminal';
import {asyncComputed} from '@vueuse/core';
import {getPickupPoint} from '~/api/order';
import {useCityStore} from '~/stores/city';
import { useDayjs } from '#dayjs'
import {daysNameFull} from '~/common/objects';

const props = defineProps({
  directionTo: {
    default: false,
    type: Boolean
  },
  order: {
    type: Object as PropType<OrderForm>,
    default: () => ({})
  }
})

const dayjs = useDayjs()
const terminalStore = useTerminalStore()
const cityStore = useCityStore()

const directionType = computed(() => {
  return props.directionTo ? 'destination' : 'dispatch'
})

const isNetwork = computed((): boolean => {
  return (props.order.isNetwork && props.directionTo) || false
})

const direction = computed(() => {
  return props.order?.directions[directionType.value]
})

const terminalGuid = computed(() => {
  return direction.value.terminalGuid
})

const gatewayAddressOptions = computed((): Record<string, string | boolean> => {
  switch (direction.value.direction) {
    case 'address':
      return { name: 'home', variant: 'primary' }
    case 'terminal':
      return { url: '/svg/vz-terminal-red.svg', noMaskedIcon: true }
    case 'pickupPoint':
      return { url: '/svg/vz-hand-cargo.svg', noMaskedIcon: true, variant: 'primary' }
  }
})

const gatewayAddressLabel = computed(() => {
  switch (direction.value.direction) {
    case 'address':
      return 'Адрес'
    case 'terminal':
      return 'Терминал'
    case 'pickupPoint':
      return 'Пункт выдачи заказа'
  }
})

const gatewayAddress = asyncComputed(async () => {
  if (!direction.value) {
    return ''
  }

  let address = ''
  switch (direction.value.direction) {
    case 'address':
      address = direction.value.address
      break;
    case 'terminal':
      address = (props.order as OrderView).directions[directionType.value].terminalName
        || terminalStore.terminalByGuid(direction.value.terminalGuid)?.name || ''
      break;
    case 'pickupPoint':
      address = (props.order as OrderView).directions[directionType.value].pickupPointName ||
        (await getPickupPoint(direction.value.pickupPointGuid || ''))?.name || ''
      break;
  }

  const location = (props.order as OrderView).directions[directionType.value].locationName ||
    (await cityStore.locationByGuid(direction.value.locationGuid))?.name || ''

  return `${location}, ${address}`
})

const gatewayDate = computed(() => {
  if (!direction.value) {
    return ''
  }

  // const date = (props.order as OrderView).directions[purpose.value].calendar.date || direction.value.calendar.date
  const date =  direction.value.calendar.date
  if (!date) {
    return ''
  }

  const dateText = dayjs(date).format('DD.MM.YYYY')
  return `Дата: ${dateText} ${daysNameFull[dayjs(date).get('day')]}`
})

const gatewayTime = computed((): string => {
  let time = 'Время: '
  if (direction.value) {
    // const shippingTerm = props.gateway?.point?.[props.gateway.point?.type]?.shippingTerm
    if (direction.value.direction !== 'address') {
      const suffix = !props.directionTo ? 'до' : 'с'
      time = time + `${suffix} ${direction.value.calendar.terminalTime || ''}`
    } else {
      if (direction.value.calendar.from) {
        time = time + `c ${direction.value.calendar.from} `
      }
      if (direction.value.calendar.to) {
        time = time + `до ${direction.value.calendar.to} `
      }
    }

    if (direction.value.calendar.fixedTime) {
      time += '(фиксированное)'
    }
  }

  return time
})

const titleCard = computed((): string => {
  if (isNetwork.value) {
    return 'Доставка в сеть'
  }
  return props.directionTo ? 'Прибытие' : 'Отправка'
})

const tradingNetworkName = computed((): string => {
  if (!isNetwork.value) {
    return ''
  }
  return (props.order as OrderView).customer?.destination?.corporation?.companyName?.value || ''
})
</script>

<style lang="scss" scoped>
.vz-direction-view--title {
  margin-bottom: 15px;
}

.vz-gateway-view {
  font-size: 14px;
  line-height: 16.5px;
  > div {
    display: flex;
    align-items: flex-start;
    &:not(:last-child) {
      margin-bottom: 15px;
    }
    .vz-icon {
      margin-right: 5px;
    }
  }
}
</style>

